import { WmButton } from "@watermarkinsights/ripple-react";
import translate from "../../../../i18n/translate";
import { useEffect, useReducer } from "react";
import { sendRequestWithPayload, sendRequest } from "@/common/http";
import { useIntl, FormattedMessage } from "react-intl";
import { downloadPDF, useFormatMessage } from "@/features/common/helper";
import WidgetLoader from "../../../common/elements/WidgetLoader";
import { OutcomesTerminology } from "@/contexts/AppContext";

interface HeaderProps {
  outcomesTerminology?: OutcomesTerminology | null;
}

interface ExportResultSet {
  pdf_file_bytes: string;
}
interface TokenResultSet {
  token: string;
}
interface HeaderAction {
  type: string;
  token: string | null;
  data: boolean;
}

interface HeaderState {
  token: string | null;
  isExportInProgress: boolean;
}

const defaultHeaderAction = {
  type: "",
  token: "",
  data: false
};

const reducer = (state: HeaderState, action: HeaderAction) => {
  switch (action.type) {
    case "setToken":
      return { ...state, token: action.token };
    case "setExportInProgress":
      return { ...state, isExportInProgress: action.data };
    default:
      return state;
  }
};

const initialState = {
  token: null,
  isExportInProgress: false
};
export default function Header({ outcomesTerminology }: HeaderProps): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initialState);
  const formatMessage = useFormatMessage();

  const subHeader = useIntl().formatMessage({
    id: "app.strategy.exportHtmlHeader"
  });
  const header = formatMessage(
    { id: "app.missionAndOutcomes.title" },
    { outcome_plural: outcomesTerminology?.outcomes?.plural || "..." }
  );

  const getOutcomeText = () => {
    if (!outcomesTerminology) {
      return (
        <span className="heading-loader-wrapper">
          <WidgetLoader
            loaderContainerClasses="loader-container"
            loaderClasses="widget-loader"
          />
        </span>
      );
    }
    return outcomesTerminology?.outcomes?.plural || "...";
  };
  function exportToPdf() {
    let mainContent = document.querySelector(
      '[data-export-id="mission-outcome-export"]'
    );
    let downloadText = document.querySelector(".download-text");
    if (downloadText) {
      downloadText.setAttribute("tabindex", "0");
      (downloadText as HTMLElement).focus();
    }
    if (mainContent) {
      let clone = mainContent.cloneNode(true);

      let wrapperDiv = document.createElement("div");
      wrapperDiv.appendChild(clone);

      let formData = new FormData();
      formData.append("html", wrapperDiv.outerHTML);
      formData.append("header", encodeURIComponent(header));
      formData.append("sub_header", encodeURIComponent(subHeader));
      formData.append("export_id", "mission-outcome-export");
      formData.append("_csrf_token", state.token ? state.token : "");

      const applyResult = (result: ExportResultSet) => {
        const outcomesLabel = (
          outcomesTerminology?.outcomes?.plural || "Outcomes"
        ).replace(/\s+/g, "_");
        const pdfName = `Mission_and_${outcomesLabel}_Definition_Insights`;
        downloadPDF(result.pdf_file_bytes, pdfName);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      const onError = (error: string) => {
        // Log error in console for debugging
        console.log("API Error:", error);
        dispatch({ ...defaultHeaderAction, type: "setToken", token: null });
        dispatch({
          ...defaultHeaderAction,
          type: "setExportInProgress",
          data: false
        });
      };
      sendRequestWithPayload(
        {
          uri: "/export",
          body: formData
        },
        applyResult,
        onError
      );
    }
  }

  useEffect(() => {
    if (state.isExportInProgress) {
      sendRequest(
        {
          uri: "/export/token",
          camelize: true
        },
        (result: TokenResultSet) => {
          dispatch({
            ...defaultHeaderAction,
            type: "setToken",
            token: result.token
          });
        }
      );
    }
  }, [state.isExportInProgress]);
  useEffect(() => {
    if (state.token && state.isExportInProgress) {
      exportToPdf();
    }
  }, [state.isExportInProgress, state.token]);
  return (
    <>
      <section
        className="page-banner-container -mainheader -withbreadcrumbs only-breadcrumbs header-padding"
        id="page-header"
      >
        <div className="flex-row export-container">
          <div>
            <div>
              <h2 className="org-name uppercase org-name-default-margins light-font">
                <FormattedMessage
                  id="app.missionAndOutcomes.title"
                  values={{
                    outcome_plural: getOutcomeText()
                  }}
                />
              </h2>
            </div>
            <div className="identity-details">
              <div className="page-header -titleonly">
                <h2 id="main-heading" className="plan-title-header">{subHeader}</h2>
              </div>
            </div>
          </div>
          <div className="flex-row export-button">
            {state.isExportInProgress && (
              <span className="download-text">
                {translate(
                  "app.planParticipation.exportPdf.downloadReportText"
                )}
              </span>
            )}
            <div className="button-container">
              <WmButton
                button-type="secondary"
                icon="f1da"
                icon-size="20px"
                onClick={() =>
                  dispatch({
                    ...defaultHeaderAction,
                    type: "setExportInProgress",
                    data: true
                  })
                }
                disabled={state.isExportInProgress}
              >
                {translate("app.downloadHtml")}
              </WmButton>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
